import { useState, type MouseEvent } from "react";
import { Divider, Menu, MenuItem } from "@mui/material";
import { ChevronRight, Download } from "@mui/icons-material";

import type { JobsDashboardTableViewModel, DownloadMenuContext, FileMenuItem } from "../JobsDashboardTable.types";
import { DOWNLOAD_FORMAT_OPTIONS } from "../JobsDashboardTable.types";
import { isGeoFileMenuItem } from "../JobsDashboardTable.helpers";

export interface DownloadMenuProps {
	vm: JobsDashboardTableViewModel;
	downloadCtx: DownloadMenuContext;
}

interface FormatMenuState {
	element: HTMLElement;
	menuItem: FileMenuItem;
	rowName: string;
}

export function DownloadMenu({ vm, downloadCtx }: DownloadMenuProps) {
	const [formatMenu, setFormatMenu] = useState<FormatMenuState | null>(null);
	const isSuperUser = vm.user?.isSuperUser ?? false;

	function closeAll() {
		setFormatMenu(null);
		vm.setActionMenuElement(null);
	}

	function handleGeoItemClick(event: MouseEvent<HTMLLIElement>, menuItem: FileMenuItem, rowName: string) {
		setFormatMenu({ element: event.currentTarget, menuItem, rowName });
	}

	return (
		<>
			<Menu anchorEl={vm.actionMenuElement} open={Boolean(vm.actionMenuElement)} onClose={closeAll}>
				<MenuItem key="header">
					<strong>Files for {downloadCtx.itemName}</strong>
				</MenuItem>
				<MenuItem
					key="all-files"
					onClick={vm.downloadFiles(
						vm.jobs.map((job) => job.id),
						downloadCtx.allFiles,
						"All Files",
						downloadCtx.itemName,
					)}
				>
					<Download /> All Files
				</MenuItem>
				{downloadCtx.fileItems.map(({ menuItem, files, rowName }) => {
					const isGeo = isGeoFileMenuItem(menuItem);
					const hasFiles = files.length > 0;

					if (!hasFiles) {
						return (
							<MenuItem key={menuItem.property} disabled>
								<Download /> {menuItem.label}
							</MenuItem>
						);
					}

					// SuperUser + geo file → open format sub-menu
					if (isSuperUser && isGeo) {
						return (
							<MenuItem
								key={menuItem.label.toLowerCase().replace(/\s+/g, "-")}
								onClick={(e) => handleGeoItemClick(e, menuItem, rowName)}
							>
								<Download /> {menuItem.label}
								<ChevronRight sx={{ ml: "auto" }} />
							</MenuItem>
						);
					}

					// Default: direct download
					return (
						<MenuItem
							key={menuItem.label.toLowerCase().replace(/\s+/g, "-")}
							onClick={vm.downloadFiles([vm.actionMenuRow?.original?.id ?? ""], files, menuItem.label, rowName)}
						>
							<Download /> {menuItem.label}
						</MenuItem>
					);
				})}
				{downloadCtx.showMergedCSV && (
					<MenuItem
						key="sampling-csv-merged"
						onClick={vm.downloadFiles(
							vm.selectedJobs.filter((j) => !!j).map((job) => job.id),
							downloadCtx.mergedCSVFiles,
							"Sampling CSV (Merged)",
							downloadCtx.itemName,
							true,
						)}
					>
						<Download /> Sampling CSV (Merged)
					</MenuItem>
				)}
				{vm.user?.isDev && downloadCtx.jobId !== "" && (
					<MenuItem key="job-id" onClick={vm.copyJobId}>
						<strong>{downloadCtx.jobId}</strong>
					</MenuItem>
				)}
			</Menu>

			{/* Format sub-menu for geo files */}
			<Menu
				anchorEl={formatMenu?.element ?? null}
				open={Boolean(formatMenu)}
				onClose={() => setFormatMenu(null)}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
			>
				{formatMenu &&
					DOWNLOAD_FORMAT_OPTIONS.map((opt) => (
						<MenuItem
							key={opt.value}
							onClick={() => {
								vm.downloadGeoInFormat(formatMenu.menuItem, opt.value, formatMenu.rowName)();
								closeAll();
							}}
						>
							{opt.label}
						</MenuItem>
					))}
				{formatMenu && downloadCtx.isGroupRow && (
					<>
						<Divider />
						{DOWNLOAD_FORMAT_OPTIONS.map((opt) => (
							<MenuItem
								key={`merged-${opt.value}`}
								onClick={() => {
									vm.downloadMergedGeoInFormat(formatMenu.menuItem, opt.value, formatMenu.rowName)();
									closeAll();
								}}
							>
								Merged as {opt.label.replace("Download as ", "")}
							</MenuItem>
						))}
					</>
				)}
			</Menu>
		</>
	);
}
